/**
 * promotionGovernance — QST-365 (LOG-ONLY, read-only)
 *
 * Single source of truth for the standardized **readiness vocabulary** and
 * **CTO action vocabulary** rendered by the Accountability dashboard. The
 * Accountability tab is the read-only governance layer that answers one
 * question per feature row: *"can this evidence influence live trades yet?"*
 *
 * # Protected contracts (do not break)
 *
 * This module is governance-only. It MUST NOT influence:
 *   - FBE Forecast top-level tab / session forecast logic.
 *   - FBE trader guidance wording, Convex fields, and calibration logic.
 *   - Kronos/HMM forecast generation or snapshot inputs.
 *   - Entries, exits, gates, sizing, stops, targets, Telegram alerts,
 *     agent scoring thresholds.
 *
 * The vocabulary is intentionally **conservative**: no readiness label and
 * no CTO action implies automatic promotion or live execution. The only
 * trading-execution implication carried by the vocabulary is *Keep enforced*
 * for features that are ALREADY hard gates in production
 * (e.g. SMA-50). New live execution must always go through Sabastian and a
 * separate ticket; this module never promotes.
 */

/* ------------------------------------------------------------------ */
/* Vocabulary types                                                   */
/* ------------------------------------------------------------------ */

/**
 * Conservative readiness vocabulary. Every Accountability evidence row uses
 * exactly one of these labels.
 *
 *   - ``execution_grade``: feature is already a live hard gate / enforced
 *     path. Listed for governance visibility only; no behavior change.
 *   - ``review_candidate``: live path is producing material evidence with
 *     ambiguous edge; Sabastian / quant review required before any change.
 *   - ``collecting``: live or LOG-ONLY path is wired and producing samples
 *     but the sample is too small / too noisy to decide.
 *   - ``watch_only``: read-only context surface — never gates, never alerts,
 *     never promoted.
 *   - ``noise_rejected``: tested and rejected as a promotion candidate.
 *     Kept LOG-ONLY for attribution / regression evidence; no live path.
 */
export type Readiness =
  | "execution_grade"
  | "review_candidate"
  | "collecting"
  | "watch_only"
  | "noise_rejected";

/**
 * Conservative CTO action vocabulary. Every Accountability evidence row has
 * exactly one CTO action.
 *
 *   - ``keep_enforced``: feature is ALREADY a live hard gate / enforced.
 *     No change — preserve the live behavior.
 *   - ``keep_log_only``: feature is LOG-ONLY today; keep collecting evidence
 *     without changing trading behavior.
 *   - ``collect_more``: sample is too small or too inconsistent — extend
 *     the LOG-ONLY collection window.
 *   - ``manual_review``: enough evidence exists to warrant a quant review,
 *     but DO NOT promote without an explicit ticket + sign-off.
 *   - ``investigate_anomaly``: evidence diverges from expectation in a way
 *     that needs root-cause investigation before any decision.
 *   - ``do_not_promote``: explicit governance lock — feature must remain
 *     non-executable / non-gating until specific external criteria pass
 *     (e.g. QST-103 regime validation flag unlock criteria).
 *
 * NONE of these actions imply that a live trade behavior should change as
 * a direct consequence of THIS module. Each is a governance label only.
 */
export type CtoAction =
  | "keep_enforced"
  | "keep_log_only"
  | "collect_more"
  | "manual_review"
  | "investigate_anomaly"
  | "do_not_promote";

/** Full ordered set of readiness labels, used by UI dropdowns and tests. */
export const READINESS_VALUES: readonly Readiness[] = [
  "execution_grade",
  "review_candidate",
  "collecting",
  "watch_only",
  "noise_rejected",
] as const;

/** Full ordered set of CTO actions, used by UI dropdowns and tests. */
export const CTO_ACTION_VALUES: readonly CtoAction[] = [
  "keep_enforced",
  "keep_log_only",
  "collect_more",
  "manual_review",
  "investigate_anomaly",
  "do_not_promote",
] as const;

/**
 * Human-readable label for each readiness value. UI components should
 * always read through this mapping instead of hard-coding strings.
 */
export const READINESS_LABELS: Record<Readiness, string> = {
  execution_grade: "Execution-grade",
  review_candidate: "Review candidate",
  collecting: "Collecting",
  watch_only: "Watch-only",
  noise_rejected: "Noise / rejected",
};

/** Short, conservative explanation of each readiness state. */
export const READINESS_DESCRIPTIONS: Record<Readiness, string> = {
  execution_grade:
    "Already a live hard gate / enforced path. Listed for governance visibility only — no change implied.",
  review_candidate:
    "Live path is producing material evidence with ambiguous edge. Manual quant review required before any change.",
  collecting:
    "Live or LOG-ONLY evidence is accumulating; sample too small to decide. Keep collecting; no change.",
  watch_only:
    "Read-only context. Never gates, never alerts, never promoted by this layer.",
  noise_rejected:
    "Tested and rejected as a promotion candidate. Retained for attribution / regression evidence only.",
};

/**
 * Human-readable label for each CTO action. UI must read this map.
 */
export const CTO_ACTION_LABELS: Record<CtoAction, string> = {
  keep_enforced: "Keep enforced",
  keep_log_only: "Keep LOG-ONLY",
  collect_more: "Collect more",
  manual_review: "Manual review",
  investigate_anomaly: "Investigate anomaly",
  do_not_promote: "Do not promote",
};

/** Short, conservative description of each CTO action. */
export const CTO_ACTION_DESCRIPTIONS: Record<CtoAction, string> = {
  keep_enforced: "Preserve the live hard gate exactly as it is today.",
  keep_log_only:
    "Continue LOG-ONLY collection. Do not change entries, exits, gates, sizing, alerts, or scoring thresholds.",
  collect_more:
    "Extend the LOG-ONLY window before any review. Sample is too small or too inconsistent.",
  manual_review:
    "Enough evidence to warrant a quant review, but DO NOT promote without a separate ticket and explicit sign-off.",
  investigate_anomaly:
    "Evidence diverges from expectation. Root-cause investigate before any decision.",
  do_not_promote:
    "Governance lock. Feature must remain non-executable until the specified external criteria pass.",
};

/* ------------------------------------------------------------------ */
/* Feature governance registry                                        */
/* ------------------------------------------------------------------ */

/**
 * Tab on the Accountability dashboard a row should be surfaced under.
 *
 *   - ``all``: render the row on every Accountability sub-tab (top-of-page
 *     governance summary).
 *   - any concrete tab key: render only on that sub-tab.
 *
 * The tab keys mirror ``AccountabilityTab`` in ``App.tsx``.
 */
export type GovernanceScope =
  | "all"
  | "fbe"
  | "orb"
  | "fbd"
  | "mr"
  | "execution"
  | "regime"
  | "profitability"
  | "data";

export interface PromotionGovernanceRow {
  /** Stable id. Used as the React key. */
  id: string;
  /** Short human-readable feature name. */
  feature: string;
  /** Sub-tab this row should appear under. */
  scope: GovernanceScope;
  /** Conservative readiness label. */
  readiness: Readiness;
  /** Conservative CTO action label. */
  ctoAction: CtoAction;
  /** Short rationale shown next to the labels. No live-behavior implication. */
  rationale: string;
  /** Optional reference to a ticket / docs / module that owns the live behavior. */
  evidenceRef?: string;
}

/**
 * The promotion governance registry.
 *
 * Each entry maps a known feature to its standardized readiness label and
 * CTO action. The registry is deliberately static: it is the human-readable
 * source of truth, not a runtime decision engine. Changes here NEVER change
 * live trading behavior — entries / exits / gates / sizing / alerts /
 * scoring thresholds are not derived from this module.
 *
 * Coverage maps directly to QST-365 acceptance:
 *   - SMA-50 hard gate — execution-grade / keep enforced.
 *   - ORB long gates — ambiguous / review first.
 *   - FBD reclaim — live path, promising but underpowered / collect more.
 *   - FBD absorption trap — LOG-ONLY / collecting.
 *   - FBD bullish TA pattern — LOG-ONLY; compare pattern vs no-pattern.
 *   - MR bearish TA pattern — LOG-ONLY; compare pattern vs no-pattern.
 *   - Volume flow / order flow — LOG-ONLY attribution evidence.
 *   - Regime validation flags — locked unless QST-103 criteria pass.
 *   - Level bounce — research / no live change.
 *
 * FBE / Kronos / HMM are intentionally omitted from the registry. They are
 * protected forecast surfaces, not part of the ORB/FBD/MR promotion review.
 */
export const PROMOTION_GOVERNANCE_REGISTRY: readonly PromotionGovernanceRow[] = [
  {
    id: "sma50-gate",
    feature: "SMA-50 hard gate",
    scope: "all",
    readiness: "execution_grade",
    ctoAction: "keep_enforced",
    rationale: "Live hard gate (signal_gates.py). Attribution confirms SMA-50 alignment as the only execution-grade intel layer today.",
    evidenceRef: "QST-317",
  },
  {
    id: "orb-long-gates",
    feature: "ORB long gates",
    scope: "orb",
    readiness: "review_candidate",
    ctoAction: "manual_review",
    rationale: "Live evidence is ambiguous across regimes. Run the ORB Long Gate Review before any change.",
    evidenceRef: "QST-103 / follow-up: ORB Long Gate Review",
  },
  {
    id: "fbd-reclaim",
    feature: "FBD reclaim",
    scope: "fbd",
    readiness: "collecting",
    ctoAction: "collect_more",
    rationale: "Live path is wired and producing samples, but sample size is too small to decide direction.",
  },
  {
    id: "fbd-absorption-trap",
    feature: "FBD absorption trap",
    scope: "fbd",
    readiness: "collecting",
    ctoAction: "keep_log_only",
    rationale: "LOG-ONLY trap evidence (trap_score, signal_type=absorption_trap). Keep collecting without changing live behavior.",
    evidenceRef: "QST-331",
  },
  {
    id: "fbd-bullish-ta-pattern",
    feature: "FBD bullish TA pattern",
    scope: "fbd",
    readiness: "watch_only",
    ctoAction: "keep_log_only",
    rationale: "LOG-ONLY pattern attribution. Compare pattern vs no-pattern expectancy without gating entries.",
  },
  {
    id: "mr-bearish-ta-pattern",
    feature: "MR bearish TA pattern",
    scope: "mr",
    readiness: "watch_only",
    ctoAction: "keep_log_only",
    rationale: "LOG-ONLY pattern attribution. Compare pattern vs no-pattern expectancy without gating entries.",
  },
  {
    id: "volume-order-flow",
    feature: "Volume flow / order flow",
    scope: "all",
    readiness: "watch_only",
    ctoAction: "keep_log_only",
    rationale: "LOG-ONLY attribution evidence. Visible on the Command Center as observability; never gates or alerts trades from this layer.",
  },
  {
    id: "regime-validation-flags",
    feature: "Regime validation flags (QST-103)",
    scope: "regime",
    readiness: "watch_only",
    ctoAction: "do_not_promote",
    rationale: "All five flags remain locked until QST-103 unlockable criteria are mechanically satisfied. No flag flips here.",
    evidenceRef: "QST-103 / QST-280–QST-284",
  },
  {
    id: "level-bounce",
    feature: "Level bounce",
    scope: "all",
    readiness: "collecting",
    ctoAction: "keep_log_only",
    rationale: "Research surface only. No live entries, exits, gates, sizing, alerts, or scoring derived from level-bounce evidence.",
  },
];

/* ------------------------------------------------------------------ */
/* Read-only assertions / predicates                                  */
/* ------------------------------------------------------------------ */

/**
 * Returns ``true`` if the given readiness or CTO action implies that a
 * feature should be automatically promoted to live execution without an
 * explicit, ticketed sign-off.
 *
 * The conservative vocabulary is designed so this ALWAYS returns ``false``.
 * Tests pin the invariant. If a future contributor adds a new readiness or
 * CTO action that bypasses Sabastian review, the test will fail.
 *
 * ``keep_enforced`` carries a live-execution implication, but only as a
 * documentation/preserve marker for features that are ALREADY live hard
 * gates. It does NOT promote anything new.
 */
export function impliesAutomaticPromotion(
  value: Readiness | CtoAction,
): boolean {
  // Every value in the conservative vocabulary requires explicit human
  // sign-off before any live trade behavior change. None promote.
  // The parameter is kept in the signature so future contributors who want
  // to add an "auto-promote" label collide with this function and the
  // associated tests instead of silently bypassing review.
  void value;
  return false;
}

/**
 * Returns ``true`` if the row implies any live trade-behavior change as a
 * direct consequence of being listed here. Always ``false`` for the
 * standardized registry — locked by ``promotionGovernance.test.ts``.
 */
export function rowImpliesLiveTradingChange(
  row: PromotionGovernanceRow,
): boolean {
  // Even ``keep_enforced`` rows do not imply a change — they describe the
  // existing live state and require no action from this module.
  void row;
  return false;
}

/**
 * Filter the registry to the rows applicable to a given Accountability
 * sub-tab. Rows with ``scope: "all"`` are always included. Used by the
 * governance panel to render a per-tab summary.
 */
export function selectGovernanceRowsForTab(
  tab: GovernanceScope,
): PromotionGovernanceRow[] {
  if (tab === "fbe") return [];
  return PROMOTION_GOVERNANCE_REGISTRY.filter(
    (row) => row.scope === "all" || row.scope === tab,
  );
}
